import { useState } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, StatCard, StatCardList, Table } from '@evogard/ui';
import { Chart } from '@evogard/ui/chart';
import { format, formatDate, loadFailureMessage, sum, type Money } from '@evogard/domain';
import type { AwardDaily, AwardDailyRow } from '@evogard/contract';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { PlateLink, refusalLabel } from './common.js';
import { centsOf, compactMoney } from './reportFormat.js';

type Measured = Extract<AwardDaily, { estado: 'medido' }>;

/**
 * A consulta do dia a dia de uma premiação — uma só, para a tela do relatório
 * (o cabeçalho lê o nome dela) e o corpo dividirem o mesmo cache.
 */
export function awardDailyQuery(
  tenantId: number | undefined,
  consultant: string | undefined,
  award: number,
  month: string,
) {
  return queryOptions({
    queryKey: [
      'sales',
      'management',
      'portfolio-daily',
      tenantId,
      consultant ?? 'me',
      award,
      month,
    ],
    queryFn: async () => {
      const r = await api.sales.managementPortfolioDaily({
        query: consultant === undefined ? { award, month } : { award, month, consultant },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o relatório'));
      return r.body;
    },
  });
}
type Day = Measured['days'][number];
type Side = 'gained' | 'lost';

/*
 * O KPI selecionado só ganha uma borda laranja bem fraca, quase cinza, sem o
 * fundo laranja do `StatCard` pressionado (28/10/2026, pedido do dono): os dois
 * cartões já têm cor própria na pastilha, e o fundo competia com ela.
 */
const QUIET_PRESSED =
  'hover:border-primaria/25 aria-pressed:border-primaria/25 aria-pressed:bg-superficie';

/**
 * O RELATÓRIO da premiação aberto pelo Início do Comercial (28/10/2026, pedido
 * do dono): o gráfico, os dois KPIs e a tabela "Dados" — nesta ordem, e mais
 * nada. O resumo, as contagens e a lista longa do relatório ficam para quem
 * chega pelo Painel de Gestão.
 *
 * ## Tudo em reais DA PREMIAÇÃO
 *
 * O gráfico, os KPIs e a coluna "Premiação" da tabela saem da mesma repartição
 * feita pela API: o valor do painel ("Você já garantiu" e "Você está
 * perdendo") dividido entre as linhas — ver o contrato. Por isso a tabela soma
 * o KPI, e o KPI soma o gráfico.
 *
 * As cores dizem o estado: verde é o ganho; o que se deixa de ganhar fica no
 * laranja da ordem, e não no vermelho, porque boleto que vence amanhã não está
 * atrasado. Os KPIs vestem as mesmas cores (`legend`).
 *
 * ## O KPI troca a tabela
 *
 * "Deixa de ganhar" mostra as linhas em aberto; "Premiação ganha" volta para as
 * que geraram o valor. Na direta e na antecipada não há "deixa de ganhar": o
 * KPI diz que não se aplica e não troca nada.
 */
export function AwardHomeReport({
  award,
  month,
  consultant,
}: {
  award: number;
  month: string;
  consultant: string | undefined;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const [side, setSide] = useState<Side>('gained');
  /*
   * A coluna escolhida no gráfico (5 a 30) — filtra os KPIs e a tabela (28/10/2026,
   * pedido do dono). Clicar de novo na mesma coluna, ou em "Limpar", volta ao
   * mês inteiro.
   */
  const [day, setDay] = useState<number | null>(null);

  const query = useQuery(awardDailyQuery(tenantId, consultant, award, month));

  const body = query.data;
  if (body?.estado === 'sem_total') {
    return (
      <Alert tone="alerta" title="Esta premiação não foi apurada no mês">
        {body.reason === null
          ? 'Não há valor a mostrar por dia.'
          : `Motivo: ${refusalLabel(body.reason)}.`}
      </Alert>
    );
  }

  /* O `as` é o estreitamento que o tipo do contrato compilado não faz sozinho. */
  const daily = body?.estado === 'medido' ? (body as Measured) : undefined;
  const losing = daily?.lost === 'apurado';
  const shown: Side = losing ? side : 'gained';

  /*
   * Os totais dos KPIs são a soma do gráfico MAIS a parte sem data, que tem
   * valor e só não tem dia onde ser desenhada — senão o KPI diria menos do que
   * o painel. Com uma coluna escolhida, os KPIs são os daquela coluna.
   */
  const picked = day === null ? undefined : daily?.days.find((d) => d.day === day);
  const totals =
    daily === undefined
      ? undefined
      : picked !== undefined
        ? { gained: centsOf(picked.paid), lost: centsOf(picked.open) }
        : {
            gained: sum(
              centsOf(daily.undated.paid),
              ...daily.days.map((d) => centsOf(d.paid)),
            ),
            lost: sum(
              centsOf(daily.undated.open),
              ...daily.days.map((d) => centsOf(d.open)),
            ),
          };

  return (
    <>
      <Chart<Day>
        kind="barra"
        title={undefined}
        label="Premiação ganha e deixada de ganhar nos dias 5, 10, 15, 20, 25 e 30 da competência"
        data={daily?.days ?? []}
        loading={query.isPending}
        error={query.error}
        empty="Nenhuma premiação nesta competência."
        category={(d) => `Dia ${d.day}`}
        series={[
          {
            key: 'ganha',
            label: 'Premiação ganha',
            color: 'fase-verde',
            value: (d) => centsOf(d.paid),
          },
          ...(losing
            ? [
                {
                  key: 'perdida',
                  label: 'Deixa de ganhar',
                  color: 'fase-laranja' as const,
                  value: (d: Day) => centsOf(d.open),
                },
              ]
            : []),
        ]}
        format={(cents) => format(cents as Money)}
        axisFormat={compactMoney}
        /* a tabela "Dados" logo abaixo já tem os números (28/10/2026, pedido do dono) */
        withoutTable
        onSelect={(d) => setDay((current) => (current === d.day ? null : d.day))}
        selected={(d) => d.day === picked?.day}
      />

      {/*
       * A escolha fora do gráfico, com o jeito de desfazê-la: a coluna não
       * recebe foco de teclado, e um filtro que só se vê apagando colunas é um
       * filtro que se esquece ligado.
       */}
      {picked !== undefined && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-texto-suave">
          <span>
            <strong className="text-texto">Dia {picked.day}</strong>
          </span>
          <Button size="sm" variant="discreto" onClick={() => setDay(null)}>
            Limpar
          </Button>
        </div>
      )}

      {totals !== undefined && daily !== undefined && (
        <StatCardList stretch>
          <StatCard
            icon="confirmado"
            legend="fase-verde"
            label="Premiação ganha"
            value={format(totals.gained)}
            onClick={() => setSide('gained')}
            pressed={shown === 'gained'}
            className={QUIET_PRESSED}
          />
          <StatCard
            icon="relogio"
            legend="fase-laranja"
            label="Deixa de ganhar"
            value={
              daily.lost === 'apurado'
                ? format(totals.lost)
                : daily.lost === 'nao_se_aplica'
                  ? 'Não se aplica'
                  : 'Não apurado'
            }
            support={
              daily.lost === 'nao_se_aplica'
                ? 'A placa em aberto ainda pode ativar dentro do prazo.'
                : daily.lost === 'recusado'
                  ? 'O lado "perdendo" desta premiação não foi apurado.'
                  : undefined
            }
            {...(losing && {
              onClick: () => setSide('lost'),
              pressed: shown === 'lost',
              className: QUIET_PRESSED,
            })}
          />
        </StatCardList>
      )}

      {daily !== undefined && (
        <Card
          title="Dados"
          descricao={
            shown === 'lost'
              ? 'Os boletos em aberto e o quanto cada um deixa de render.'
              : undefined
          }
          bleed
        >
          <Table<AwardDailyRow & { index: number }>
            label="Dados"
            items={(shown === 'lost' ? daily.rows.lost : daily.rows.gained)
              .filter((r) => picked === undefined || r.bucket === picked.day)
              .map((r, index) => ({ ...r, index }))}
            /* a linha não tem identificador: o mesmo boleto-placa pode repetir */
            rowKey={(r) => String(r.index)}
            empty={
              shown === 'lost'
                ? picked === undefined
                  ? 'Nenhum boleto em aberto nesta competência.'
                  : 'Nenhum boleto em aberto nesta coluna.'
                : picked === undefined
                  ? 'Nenhuma linha gerou premiação nesta competência.'
                  : 'Nenhuma linha gerou premiação nesta coluna.'
            }
            columns={[
              { title: 'Cliente', cell: (r) => r.memberName ?? '—' },
              {
                title: 'Placa',
                cell: (r) =>
                  r.plate === null ? (
                    '—'
                  ) : (
                    <PlateLink
                      plate={r.plate}
                      memberCode={r.memberCode}
                      vehicleCode={r.vehicleCode}
                      consultant={r.consultantCode ?? consultant}
                    />
                  ),
              },
              { title: 'Situação do veículo', cell: (r) => r.vehicleStatus ?? '—' },
              { title: 'Situação do boleto', cell: (r) => r.boletoStatus ?? '—' },
              /*
               * No lado "deixa de ganhar" a data é o VENCIMENTO: o boleto em
               * aberto não tem pagamento, e uma coluna inteira de travessões é
               * o leitor procurando o que não existe.
               */
              shown === 'lost'
                ? {
                    title: 'Vencimento',
                    cell: (r) => (r.dueDate === null ? '—' : formatDate(r.dueDate)),
                  }
                : {
                    title: 'Pagamento',
                    cell: (r) => (r.paidOn === null ? '—' : formatDate(r.paidOn)),
                  },
              {
                title: 'Premiação',
                alignment: 'fim',
                cell: (r) => <MoneyValue value={r.award} />,
              },
            ]}
          />
        </Card>
      )}
    </>
  );
}
